import React, { useState, useEffect, useCallback } from 'react';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { Eye, CheckCircle, Filter, Trash2, Download } from 'lucide-react';
import { BuildingOption } from '../../api/feeCategoriesApi';
import { useNavigate } from 'react-router-dom';
import { feedbacksApi, Feedback } from '../../api/feedbacksApi';
import { apartmentsApi, ApartmentGroup } from '../../api/apartmentsApi';
import { getStaffMembers } from '../../api/tasksApi';
import { User } from '../../types';
import { FeedbackDetailsModal } from '../../components/modals/FeedbackDetailsModal';
import { formatFeedbackDateTime, formatProcessingDuration } from '../../utils/feedbackDateTime';
import { downloadCsv } from '../../utils/csvExport';
import { UNASSIGNED_APARTMENT_GROUP_ID } from '../../constants/apartmentGroups';
import { AsyncSearchPicker } from '../../components/common/AsyncSearchPicker';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';

interface Building {
  id: string;
  name: string;
}

interface FeedbackCategory {
  id: string;
  name: string;
  code: string;
}

const FeedbackList: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
  
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [buildings, setBuildings] = useState<BuildingOption[]>([]);
  const [categories, setCategories] = useState<FeedbackCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    inProgress: 0,
    completed: 0,
  });
  
  // Filters
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedPriority, setSelectedPriority] = useState<string>('');
  const [selectedApartmentGroupId, setSelectedApartmentGroupId] = useState<string>('');
  const [selectedApartmentId, setSelectedApartmentId] = useState<string>('');
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<string>('');
  const [selectedApartmentGroupLabel, setSelectedApartmentGroupLabel] = useState('');
  const [selectedApartmentLabel, setSelectedApartmentLabel] = useState('');
  const [selectedAssigneeLabel, setSelectedAssigneeLabel] = useState('');
  const [apartmentGroups, setApartmentGroups] = useState<ApartmentGroup[]>([]);
  const [staffMembers, setStaffMembers] = useState<User[]>([]);
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState<Feedback | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deadline, setDeadline] = useState('');
  const [feedbackToConfirm, setFeedbackToConfirm] = useState<Feedback | null>(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [feedbackToRate, setFeedbackToRate] = useState<Feedback | null>(null);
  const [ratingLoading, setRatingLoading] = useState(false);

  useEffect(() => {
    fetchBuildings();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchFeedbacks();
  }, [
    currentPage,
    selectedBuildingId,
    selectedStatus,
    selectedCategoryId,
    selectedPriority,
    selectedApartmentGroupId,
    selectedApartmentId,
    selectedAssigneeId,
  ]);

  useEffect(() => {
    if (!selectedBuildingId) {
      setApartmentGroups([]);
      setStaffMembers([]);
      setSummary({ total: 0, pending: 0, confirmed: 0, inProgress: 0, completed: 0 });
      return;
    }

    const loadFilterOptions = async () => {
      try {
        const [groups, staff] = await Promise.all([
          apartmentsApi.getApartmentGroups(selectedBuildingId),
          getStaffMembers(),
        ]);
        setApartmentGroups(groups);
        setStaffMembers(staff);
      } catch (error) {
        console.error('Error loading filter options:', error);
      }
    };

    loadFilterOptions();
    setSelectedApartmentGroupId('');
    setSelectedApartmentId('');
    setSelectedAssigneeId('');
    setSelectedApartmentGroupLabel('');
    setSelectedApartmentLabel('');
    setSelectedAssigneeLabel('');
    setSelectedStatus('');
    setSelectedCategoryId('');
    setSelectedPriority('');
    setCurrentPage(1);
  }, [selectedBuildingId]);

  useEffect(() => {
    fetchSummary();
  }, [
    selectedBuildingId,
    selectedCategoryId,
    selectedPriority,
    selectedApartmentGroupId,
    selectedApartmentId,
    selectedAssigneeId,
  ]);

  const searchApartmentGroups = useCallback(async (query: string) => {
    const q = query.toLowerCase();
    const results = apartmentGroups
      .filter((group) => group.name.toLowerCase().includes(q))
      .slice(0, 20)
      .map((group) => ({ id: group.id, label: group.name }));

    if (t('unassignedApartmentGroup').toLowerCase().includes(q)) {
      results.unshift({
        id: UNASSIGNED_APARTMENT_GROUP_ID,
        label: t('unassignedApartmentGroup'),
      });
    }

    return results;
  }, [apartmentGroups, t]);

  const searchApartments = useCallback(async (query: string) => {
    const result = await apartmentsApi.getApartmentsPage({
      buildingId: selectedBuildingId,
      search: query,
      limit: 20,
    });

    let data = result.data;
    if (selectedApartmentGroupId) {
      if (selectedApartmentGroupId === UNASSIGNED_APARTMENT_GROUP_ID) {
        data = data.filter((apartment) => !apartment.groupId);
      } else {
        data = data.filter((apartment) => apartment.groupId === selectedApartmentGroupId);
      }
    }

    return data.map((apartment) => ({
      id: apartment.id,
      label: apartment.code,
      subtitle: apartment.ownerName,
    }));
  }, [selectedBuildingId, selectedApartmentGroupId]);

  const searchStaff = useCallback(async (query: string) => {
    const q = query.toLowerCase();
    return staffMembers
      .filter(
        (staff) =>
          staff.name.toLowerCase().includes(q) ||
          (staff.email || '').toLowerCase().includes(q),
      )
      .slice(0, 20)
      .map((staff) => ({
        id: staff.id,
        label: staff.name,
        subtitle: staff.email,
      }));
  }, [staffMembers]);

  const listFilterParams = () => ({
    buildingId: selectedBuildingId,
    status: selectedStatus || undefined,
    categoryId: selectedCategoryId || undefined,
    priority: selectedPriority || undefined,
    apartmentGroupId: selectedApartmentGroupId || undefined,
    apartmentId: selectedApartmentId || undefined,
    assigneeId: selectedAssigneeId || undefined,
  });

  const summaryFilterParams = () => ({
    buildingId: selectedBuildingId,
    categoryId: selectedCategoryId || undefined,
    priority: selectedPriority || undefined,
    apartmentGroupId: selectedApartmentGroupId || undefined,
    apartmentId: selectedApartmentId || undefined,
    assigneeId: selectedAssigneeId || undefined,
  });

  const fetchBuildings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/buildings`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setBuildings(data);
        setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/feedback-categories`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSummary = async () => {
    if (!selectedBuildingId) {
      setSummary({ total: 0, pending: 0, confirmed: 0, inProgress: 0, completed: 0 });
      return;
    }

    try {
      const data = await feedbacksApi.getListSummary(summaryFilterParams());
      setSummary({
        total: data.total,
        pending: data.pending,
        confirmed: data.confirmed,
        inProgress: data.inProgress,
        completed: data.completed,
      });
    } catch (error) {
      console.error('Error fetching feedback summary:', error);
    }
  };

  const fetchFeedbacks = async () => {
    if (!selectedBuildingId) {
      setFeedbacks([]);
      setTotalPages(0);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const data = await feedbacksApi.getFeedbacks({
        ...listFilterParams(),
        page: currentPage,
        limit: 10,
      });

      setFeedbacks(data.data);
      setTotalPages(data.meta?.totalPages ?? 0);
      setTotal(data.meta?.total ?? 0);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      toast.error(t('errorLoadingFeedbacks'));
    } finally {
      setLoading(false);
    }
  };

  const handleApartmentGroupChange = (groupId: string, label = '') => {
    setSelectedApartmentGroupId(groupId);
    setSelectedApartmentGroupLabel(label);
    setSelectedApartmentId('');
    setSelectedApartmentLabel('');
    setCurrentPage(1);
  };

  const handleStatusCardClick = (status: string) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  const handleExport = async () => {
    if (!selectedBuildingId || exporting) return;

    setExporting(true);
    try {
      const allFeedbacks: Feedback[] = [];
      let page = 1;
      let totalExportPages = 1;

      while (page <= totalExportPages) {
        const result = await feedbacksApi.getFeedbacks({
          ...listFilterParams(),
          page,
          limit: 500,
        });
        allFeedbacks.push(...result.data);
        totalExportPages = result.meta?.totalPages ?? 1;
        page += 1;
      }

      const headers = [
        t('feedbackId'),
        t('feedbackContent'),
        t('feedbackApartment'),
        t('feedbackCategory'),
        t('feedbackPriority'),
        t('feedbackStatus'),
        t('feedbackDeadline'),
        t('feedbackCompletedBy'),
        t('feedbackProcessingTime'),
        t('feedbackCompletedDate'),
      ];

      const rows = allFeedbacks.map((feedback) => [
        feedback.feedbackId,
        feedback.content,
        feedback.apartment.code,
        feedback.category.name,
        feedback.priority,
        feedback.status,
        feedback.deadline ? formatFeedbackDateTime(feedback.deadline) : '',
        feedback.completedBy?.name || '',
        getProcessingTimeDisplay(feedback),
        feedback.completedAt ? formatFeedbackDateTime(feedback.completedAt) : '',
      ]);

      const buildingName =
        buildings.find((b) => b.id === selectedBuildingId)?.name || 'feedbacks';
      downloadCsv(headers, rows, `feedbacks-${buildingName}-${new Date().toISOString().slice(0, 10)}.csv`);
      toast.success(t('exportDataSuccess'));
    } catch (error) {
      console.error('Error exporting feedbacks:', error);
      toast.error(t('errorLoadingFeedbacks'));
    } finally {
      setExporting(false);
    }
  };

  const statCardClass = (active: boolean) =>
    `bg-white rounded-lg shadow p-4 cursor-pointer transition-all hover:shadow-md ${
      active ? 'ring-2 ring-blue-500' : ''
    }`;

  const handleConfirmFeedback = (feedback: Feedback) => {
    setFeedbackToConfirm(feedback);
    setShowConfirmModal(true);
    setDeadline('');
  };

  const confirmFeedback = async () => {
    if (!feedbackToConfirm || confirming || !deadline) return;
    setConfirming(true);
    try {
      const response = await fetch(`${API_BASE_URL}/feedbacks/${feedbackToConfirm.id}/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ deadline }),
      });
      if (response.ok) {
        toast.success(t('feedbackConfirmedSuccess'));
        fetchFeedbacks();
        setShowConfirmModal(false);
        setDeadline('');
        setFeedbackToConfirm(null);
      } else {
        toast.error(t('errorUpdatingFeedback'));
      }
    } catch (error) {
      console.error('Error confirming feedback:', error);
      toast.error(t('errorUpdatingFeedback'));
    } finally {
      setConfirming(false);
    }
  };

  const handleCompleteFeedback = async (feedback: Feedback) => {
    if (!window.confirm(t('confirmCompleteFeedback'))) return;

    try {
      const response = await fetch(`${API_BASE_URL}/feedbacks/${feedback.id}/complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success(t('feedbackCompletedSuccess'));
        fetchFeedbacks();
      } else {
        toast.error(t('errorUpdatingFeedback'));
      }
    } catch (error) {
      console.error('Error completing feedback:', error);
      toast.error(t('errorUpdatingFeedback'));
    }
  };

  const handleDeleteFeedback = async (feedback: Feedback) => {
    setFeedbackToDelete(feedback);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!feedbackToDelete) return;

    setDeleting(true);
    try {
      await feedbacksApi.deleteFeedback(feedbackToDelete.id);
      
      // Refresh the feedbacks list
      await fetchFeedbacks();
      setDeleteModalOpen(false);
      setFeedbackToDelete(null);
    } catch (error) {
      console.error('Error deleting feedback:', error);
      alert('Có lỗi xảy ra khi xóa phản ánh');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      PENDING_CONFIRMATION: { color: 'bg-yellow-100 text-yellow-800', text: t('feedbackStatusPendingConfirmation') },
      CONFIRMED: { color: 'bg-blue-100 text-blue-800', text: t('feedbackStatusConfirmed') },
      IN_PROGRESS: { color: 'bg-orange-100 text-orange-800', text: t('feedbackStatusInProgress') },
      COMPLETED: { color: 'bg-green-100 text-green-800', text: t('feedbackStatusCompleted') },
      REJECTED: { color: 'bg-red-100 text-red-800', text: t('feedbackStatusRejected') },
      CANCELLED: { color: 'bg-gray-100 text-gray-800', text: t('feedbackStatusCancelled') },
    };

    const config = statusConfig[status] ?? {
      color: 'bg-gray-100 text-gray-800',
      text: status,
    };

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig: Record<string, { color: string; text: string }> = {
      LOW: { color: 'bg-gray-100 text-gray-800', text: t('feedbackPriorityLow') },
      MEDIUM: { color: 'bg-blue-100 text-blue-800', text: t('feedbackPriorityMedium') },
      HIGH: { color: 'bg-orange-100 text-orange-800', text: t('feedbackPriorityHigh') },
      URGENT: { color: 'bg-red-100 text-red-800', text: t('feedbackPriorityUrgent') },
    };

    const config = priorityConfig[priority] ?? {
      color: 'bg-gray-100 text-gray-800',
      text: priority,
    };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getProcessingTimeDisplay = (feedback: Feedback) => {
    if (!feedback.completedAt) return '-';
    const start =
      feedback.processingStartedAt || feedback.confirmedAt || feedback.createdAt;
    return formatProcessingDuration(start, feedback.completedAt) || '-';
  };

  const handleRateFeedback = async (feedback: Feedback) => {
    setFeedbackToRate(feedback);
    setRating(0);
    setRatingComment('');
    setShowRatingModal(true);
  };

  const submitRating = async () => {
    if (!feedbackToRate || rating === 0 || ratingLoading) return;
    
    setRatingLoading(true);
    try {
      await feedbacksApi.rateFeedback(feedbackToRate.id, rating, ratingComment);
      toast.success('Đánh giá đã được gửi thành công!');
      setShowRatingModal(false);
      setFeedbackToRate(null);
      setRating(0);
      setRatingComment('');
      await fetchFeedbacks();
    } catch (error) {
      console.error('Error rating feedback:', error);
      toast.error('Có lỗi xảy ra khi gửi đánh giá');
    } finally {
      setRatingLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('feedbackList')}</h1>
        <div className="flex gap-3">
          {selectedBuildingId && (
            <button
              onClick={handleExport}
              disabled={exporting}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Download size={20} />
              {exporting ? t('exporting') : t('exportData')}
            </button>
          )}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Filter size={20} />
            {t('filter')}
          </button>
        </div>
      </div>

      {shouldShowBuildingSelector(buildings) && (
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('selectBuilding')} *
        </label>
        <select
          value={selectedBuildingId}
          onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
          className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{t('selectBuildingPlaceholder')}</option>
          {buildings.map((building) => (
            <option key={building.id} value={building.id}>
              {building.name}
            </option>
          ))}
        </select>
      </div>
      )}

      {selectedBuildingId && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AsyncSearchPicker
              label={t('apartmentGroup')}
              placeholder={t('searchApartmentGroupPlaceholder')}
              value={selectedApartmentGroupId}
              selectedLabel={selectedApartmentGroupLabel}
              onChange={(id, option) => handleApartmentGroupChange(id, option?.label || '')}
              onSearch={searchApartmentGroups}
              minCharsHint={t('searchMinCharsHint')}
              searchingLabel={t('searching')}
              noResultsLabel={t('noSearchResults')}
            />

            <AsyncSearchPicker
              label={t('feedbackApartment')}
              placeholder={t('searchApartmentPlaceholder')}
              value={selectedApartmentId}
              selectedLabel={selectedApartmentLabel}
              onChange={(id, option) => {
                setSelectedApartmentId(id);
                setSelectedApartmentLabel(option?.label || '');
                setCurrentPage(1);
              }}
              onSearch={searchApartments}
              minCharsHint={t('searchMinCharsHint')}
              searchingLabel={t('searching')}
              noResultsLabel={t('noSearchResults')}
            />

            <AsyncSearchPicker
              label={t('feedbackResponsibleStaff')}
              placeholder={t('searchAssigneePlaceholder')}
              value={selectedAssigneeId}
              selectedLabel={selectedAssigneeLabel}
              onChange={(id, option) => {
                setSelectedAssigneeId(id);
                setSelectedAssigneeLabel(option?.label || '');
                setCurrentPage(1);
              }}
              onSearch={searchStaff}
              minCharsHint={t('searchMinCharsHint')}
              searchingLabel={t('searching')}
              noResultsLabel={t('noSearchResults')}
            />
          </div>
        </div>
      )}

      {/* Filters */}
      {showFilters && selectedBuildingId && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('feedbackStatus')}
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('allTasks')}</option>
                <option value="PENDING_CONFIRMATION">{t('feedbackStatusPendingConfirmation')}</option>
                <option value="CONFIRMED">{t('feedbackStatusConfirmed')}</option>
                <option value="IN_PROGRESS">{t('feedbackStatusInProgress')}</option>
                <option value="COMPLETED">{t('feedbackStatusCompleted')}</option>
                <option value="REJECTED">{t('feedbackStatusRejected')}</option>
                <option value="CANCELLED">{t('feedbackStatusCancelled')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('feedbackCategory')}
              </label>
              <select
                value={selectedCategoryId}
                onChange={(e) => {
                  setSelectedCategoryId(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('allCategories')}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('feedbackPriority')}
              </label>
              <select
                value={selectedPriority}
                onChange={(e) => {
                  setSelectedPriority(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('allPriorities')}</option>
                <option value="LOW">{t('feedbackPriorityLow')}</option>
                <option value="MEDIUM">{t('feedbackPriorityMedium')}</option>
                <option value="HIGH">{t('feedbackPriorityHigh')}</option>
                <option value="URGENT">{t('feedbackPriorityUrgent')}</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Show message when no building is selected */}
      {!selectedBuildingId && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <div className="text-yellow-800 text-lg font-medium mb-2">
            {t('pleaseSelectBuilding')}
          </div>
          <div className="text-yellow-600">
            {t('selectBuildingToViewFeedbacks')}
          </div>
        </div>
      )}

      {/* Statistics - only show when building is selected */}
      {selectedBuildingId && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div
            className={statCardClass(!selectedStatus)}
            onClick={() => handleStatusCardClick('')}
            title={t('totalFeedbacks')}
          >
            <div className="text-2xl font-bold text-blue-600">{summary.total}</div>
            <div className="text-sm text-gray-600">{t('totalFeedbacks')}</div>
          </div>
          <div
            className={statCardClass(selectedStatus === 'PENDING_CONFIRMATION')}
            onClick={() => handleStatusCardClick('PENDING_CONFIRMATION')}
            title={t('pendingFeedbacks')}
          >
            <div className="text-2xl font-bold text-yellow-600">{summary.pending}</div>
            <div className="text-sm text-gray-600">{t('pendingFeedbacks')}</div>
          </div>
          <div
            className={statCardClass(selectedStatus === 'CONFIRMED')}
            onClick={() => handleStatusCardClick('CONFIRMED')}
            title={t('confirmedFeedbacks')}
          >
            <div className="text-2xl font-bold text-blue-500">{summary.confirmed}</div>
            <div className="text-sm text-gray-600">{t('confirmedFeedbacks')}</div>
          </div>
          <div
            className={statCardClass(selectedStatus === 'IN_PROGRESS')}
            onClick={() => handleStatusCardClick('IN_PROGRESS')}
            title={t('inProgressFeedbacks')}
          >
            <div className="text-2xl font-bold text-orange-600">{summary.inProgress}</div>
            <div className="text-sm text-gray-600">{t('inProgressFeedbacks')}</div>
          </div>
          <div
            className={statCardClass(selectedStatus === 'COMPLETED')}
            onClick={() => handleStatusCardClick('COMPLETED')}
            title={t('completedFeedbacks')}
          >
            <div className="text-2xl font-bold text-green-600">{summary.completed}</div>
            <div className="text-sm text-gray-600">{t('completedFeedbacks')}</div>
          </div>
        </div>
      )}

      {/* Feedbacks Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feedbackId')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feedbackContent')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feedbackApartment')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feedbackCategory')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feedbackPriority')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feedbackStatus')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feedbackDeadline')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ngày duyệt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Người duyệt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feedbackCompletedBy')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feedbackProcessingTime')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feedbackCompletedDate')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Đánh giá
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('feedbackActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={14} className="px-6 py-4 text-center text-gray-500">
                    {t('loading')}...
                  </td>
                </tr>
              ) : feedbacks.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-6 py-4 text-center text-gray-500">
                    {t('noFeedbacks')}
                  </td>
                </tr>
              ) : (
                feedbacks.map((feedback) => (
                  <tr key={feedback.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 cursor-pointer underline"
                        onClick={() => { setSelectedFeedbackId(feedback.id); setModalOpen(true); }}
                        title="Xem nhanh phản ánh"
                    >
                      {feedback.feedbackId}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                      <div className="truncate" title={feedback.content}>
                        {feedback.content}
                      </div>
                      {feedback.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {feedback.tags.map((tag, index) => (
                            <span key={index} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{feedback.apartment.code}</div>
                      <div className="text-xs text-gray-500">{feedback.apartment.ownerName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {feedback.category.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPriorityBadge(feedback.priority)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(feedback.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {feedback.deadline ? formatFeedbackDateTime(feedback.deadline) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {feedback.confirmedAt ? formatFeedbackDateTime(feedback.confirmedAt) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {feedback.confirmedBy ? feedback.confirmedBy.name : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {feedback.completedBy ? feedback.completedBy.name : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getProcessingTimeDisplay(feedback)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {feedback.completedAt ? formatFeedbackDateTime(feedback.completedAt) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {feedback.rating ? (
                        <div className="flex items-center">
                          <div className="flex text-yellow-400">
                            {[...Array(5)].map((_, i) => (
                              <svg key={i} className={`w-4 h-4 ${i < feedback.rating! ? 'fill-current' : 'text-gray-300'}`} viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                          <span className="ml-1 text-gray-600">({feedback.rating}/5)</span>
                        </div>
                      ) : (
                        feedback.status === 'COMPLETED' && user?.role === 'RESIDENT' && feedback.createdBy.id === user.id ? (
                          <button
                            onClick={() => handleRateFeedback(feedback)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Đánh giá
                          </button>
                        ) : '-'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => navigate(`/feedbacks/${feedback.id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title={t('viewFeedbackDetails')}
                        >
                          <Eye size={16} />
                        </button>
                        {feedback.status === 'PENDING_CONFIRMATION' && (
                          <button
                            onClick={() => handleConfirmFeedback(feedback)}
                            className="text-green-600 hover:text-green-900"
                            title={t('confirmFeedback')}
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}
                        {feedback.status === 'CONFIRMED' && (
                          <button
                            onClick={async () => {
                              try {
                                console.log('🔄 [Status Update] Updating feedback:', feedback.id, 'to IN_PROGRESS');
                                console.log('🔄 [Status Update] Current status:', feedback.status);
                                await feedbacksApi.updateFeedbackStatus(feedback.id, 'IN_PROGRESS');
                                console.log('✅ [Status Update] Successfully updated feedback status');
                                toast.success('Phản ánh đã chuyển sang Đang xử lý!');
                                await fetchFeedbacks();
                              } catch (err) {
                                console.error('❌ [Status Update] Error updating feedback status:', err);
                                toast.error('Lỗi khi chuyển trạng thái');
                              }
                            }}
                            className="text-blue-600 hover:text-blue-800"
                            title="Bắt đầu xử lý"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7-7 7M5 5v14" />
                            </svg>
                          </button>
                        )}
                        {feedback.status === 'IN_PROGRESS' && (
                          <button
                            onClick={() => handleCompleteFeedback(feedback)}
                            className="text-purple-600 hover:text-purple-900"
                            title={t('completeFeedback')}
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}
                        {/* Only show delete button for PENDING_CONFIRMATION status */}
                        {feedback.status === 'PENDING_CONFIRMATION' && (
                          <button
                            onClick={() => handleDeleteFeedback(feedback)}
                            className="text-red-600 hover:text-red-900"
                            title="Xóa phản ánh"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}

                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {t('previous')}
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {t('next')}
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  {t('showingRecords')
                    .replace('{from}', ((currentPage - 1) * 10 + 1).toString())
                    .replace('{to}', Math.min(currentPage * 10, total).toString())
                    .replace('{total}', total.toString())}
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-2">
                Xác nhận xóa phản ánh
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Bạn có chắc chắn muốn xóa phản ánh <strong>#{feedbackToDelete?.feedbackId}</strong>? 
                  Hành động này không thể hoàn tác.
                </p>
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setDeleteModalOpen(false);
                      setFeedbackToDelete(null);
                    }}
                    disabled={deleting}
                    className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
                  >
                    Hủy
                  </button>
                  <button
                    onClick={confirmDelete}
                    disabled={deleting}
                    className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md w-full shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50"
                  >
                    {deleting ? 'Đang xóa...' : 'Xóa'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <FeedbackDetailsModal
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedFeedbackId(null); }}
        feedbackId={selectedFeedbackId || ''}
        onRefresh={fetchFeedbacks}
      />

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <CheckCircle className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Xác nhận phản ánh
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Vui lòng chọn hạn xử lý cho phản ánh này.
              </p>
              <div className="mb-4">
                <input
                  type="datetime-local"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => { setShowConfirmModal(false); setDeadline(''); setFeedbackToConfirm(null); }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Hủy
                </button>
                <button
                  onClick={confirmFeedback}
                  disabled={confirming || !deadline}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {confirming ? 'Đang xử lý...' : 'Xác nhận'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                <svg className="h-6 w-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Đánh giá phản ánh
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Vui lòng đánh giá mức độ hài lòng của bạn về việc xử lý phản ánh này.
              </p>
              
              {/* Star Rating */}
              <div className="mb-4">
                <div className="flex justify-center space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setRating(star)}
                      className={`text-2xl ${star <= rating ? 'text-yellow-400' : 'text-gray-300'} hover:text-yellow-400 transition-colors`}
                    >
                      <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {rating === 0 && 'Chọn số sao'}
                  {rating === 1 && 'Rất không hài lòng'}
                  {rating === 2 && 'Không hài lòng'}
                  {rating === 3 && 'Bình thường'}
                  {rating === 4 && 'Hài lòng'}
                  {rating === 5 && 'Rất hài lòng'}
                </p>
              </div>

              {/* Comment */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
                  Nhận xét (tùy chọn)
                </label>
                <textarea
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Chia sẻ thêm về trải nghiệm của bạn..."
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowRatingModal(false);
                    setFeedbackToRate(null);
                    setRating(0);
                    setRatingComment('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Hủy
                </button>
                <button
                  onClick={submitRating}
                  disabled={rating === 0 || ratingLoading}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {ratingLoading ? 'Đang gửi...' : 'Gửi đánh giá'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackList; 
